import React, {useState, useEffect} from "react";
import {useMutation} from "react-query";
import Alert from "react-s-alert";
import {Formik} from "formik";
import {
  Modal,
  ModalContent,
  ModalFooterButtons,
  H1,
  H3,
  Text,
  Button,
  Box,
  CheckboxItem,
  RadioGroupField,
  theme,
  H2,
} from "@reside/ui";

import {ModalName, useDashboardModalContext} from "./DashboardModalContext";
import {readResidentNameAnswer} from "../../models/AdmissionModel.helpers";
import {useDashboardAdmissionsContext} from "./DashboardAdmissionsContext";
import {AdmissionsService} from "../../services/AdmissionsService";
import {BackdropSpinner} from "../spinner";
import {CANCELLATION_REASONS} from "../delete-admission-modal/consts";
import {getWorkflowLabel} from "../start-workflow/workflowMapping";

type Values = Readonly<{
  reason: string;
  confirmed: boolean;
}>;

export const DashboardForceDeleteAdmissionModal = () => {
  const {refreshDashboard} = useDashboardAdmissionsContext();
  const {admission, isModalOpened, toggleModal} = useDashboardModalContext(
    ModalName.FORCE_DELETE,
  );
  const [isDeleted, setIsDeleted] = useState(false);

  useEffect(() => {
    if (!isModalOpened) {
      setIsDeleted(false);
    }
  }, [isModalOpened]);

  const {mutate, isLoading} = useMutation(
    ({admissionId, reason}: {admissionId: string; reason: string}) =>
      AdmissionsService.forceDelete(admissionId, reason),
    {
      onError: () => {
        Alert.error(
          `Failed to force delete the ${getWorkflowLabel(
            admission?.workflowType,
          )}`,
        );
      },
      onSuccess: () => {
        Alert.success(
          `${getWorkflowLabel(
            admission?.workflowType,
          )} was successfully force deleted.`,
        );
        setIsDeleted(true);
        setTimeout(() => {
          refreshDashboard();
        }, 500);
      },
    },
  );

  return (
    <Modal
      isOpen={isModalOpened}
      onRequestClose={isLoading ? undefined : () => toggleModal()}
      title={`Force Delete ${getWorkflowLabel(admission?.workflowType)}`}
    >
      {({closeModal}) =>
        isDeleted ? (
          <>
            <ModalContent>
              <Text as="p">
                {getWorkflowLabel(admission?.workflowType)} for{" "}
                {readResidentNameAnswer(admission as any)} has been deleted
              </Text>
              <Text as="p" color="gray100">
                {getWorkflowLabel(admission?.workflowType)} ID:{" "}
                {admission?.code}
              </Text>
              <Text as="p" style={{marginTop: "20px", fontSize: "17.5px"}}>
                This action will be logged in the audit trail.
              </Text>
            </ModalContent>
            <ModalFooterButtons>
              <Box width="100%" flexDirection="row" gap="25px">
                <Button color="primary" onClick={closeModal}>
                  Close
                </Button>
              </Box>
            </ModalFooterButtons>
          </>
        ) : (
          <Formik<Values>
            initialValues={{reason: "", confirmed: false}}
            validate={values => {
              const errors: any = {};
              if (values.confirmed && !values.reason) {
                errors.reason = "Please select a reason";
              }
              return errors;
            }}
            onSubmit={values => {
              mutate({admissionId: admission?.id, reason: values.reason});
            }}
          >
            {({submitForm, values, setFieldValue}) => (
              <>
                <BackdropSpinner active={isLoading} />
                <ModalContent style={{color: theme.color.deepOrange100}}>
                  <H1>
                    Force Delete {getWorkflowLabel(admission?.workflowType)} for{" "}
                    {admission ? readResidentNameAnswer(admission as any) : ""}
                  </H1>
                  <H3 color="gray100">
                    {getWorkflowLabel(admission?.workflowType)} ID:{" "}
                    {admission?.code}
                  </H3>

                  <H2>⚠️ &nbsp;&nbsp;Warning: are you sure?</H2>
                  <br />
                  <Text as="p" style={{fontSize: "17.5px", lineHeight: "1.25"}}>
                    This action will bypass template validations and system
                    checks.
                  </Text>

                  <div
                    style={{
                      marginTop: "25px",
                      marginBottom: "25px",
                    }}
                  >
                    <CheckboxItem
                      disabled={isLoading}
                      checked={values.confirmed}
                      onChange={event =>
                        setFieldValue("confirmed", event.target.checked)
                      }
                      label="Confirm force delete"
                    />
                  </div>
                  {values.confirmed && (
                    <div style={{color: theme.color.darkBlue80}}>
                      <RadioGroupField
                        disabled={isLoading}
                        name="reason"
                        label={<H2>Reasons for Force Delete:</H2>}
                        options={CANCELLATION_REASONS.filter(
                          cancellationReason =>
                            cancellationReason.forStatuses.length === 0 ||
                            cancellationReason.forStatuses.some(
                              status => status === admission?.status,
                            ),
                        ).map(({cancellationReason}) => ({
                          value: cancellationReason,
                          label: cancellationReason,
                        }))}
                      />
                    </div>
                  )}
                </ModalContent>
                <ModalFooterButtons>
                  <Box width="100%" flexDirection="row" gap="25px">
                    <Button
                      type="submit"
                      color="danger"
                      disabled={
                        isLoading || !values.confirmed || !values.reason
                      }
                      onClick={submitForm}
                    >
                      {isLoading ? "Force Deleting..." : "Force Delete"}
                    </Button>
                    <Button
                      outline
                      color="primary"
                      disabled={isLoading}
                      onClick={closeModal}
                    >
                      Cancel
                    </Button>
                  </Box>
                </ModalFooterButtons>
              </>
            )}
          </Formik>
        )
      }
    </Modal>
  );
};
